import React from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Star, ExternalLink, Zap } from 'lucide-react';
import { Tool } from '../data/tools';
import { Card, CardContent, CardTitle, CardDescription } from './ui/Card';
import { SearchHighlight } from './SearchHighlight';
import { useLanguageRouter } from '../utils/i18nRouter';

interface SearchResultCardProps {
  tool: Tool;
  searchQuery: string;
  matchedFields: string[];
  score: number;
  rank: number;
}

export const SearchResultCard: React.FC<SearchResultCardProps> = ({
  tool,
  searchQuery,
  matchedFields,
  score,
  rank
}) => {
  const { t } = useTranslation();
  const { getLocalizedPath } = useLanguageRouter();
  const ToolIcon = tool.icon;

  const getMatchBadgeColor = (field: string) => {
    switch (field) {
      case 'name':
        return 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400';
      case 'description':
        return 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-400';
      case 'keywords':
        return 'bg-purple-100 text-purple-800 dark:bg-purple-900/30 dark:text-purple-400';
      case 'longDescription':
        return 'bg-orange-100 text-orange-800 dark:bg-orange-900/30 dark:text-orange-400';
      default:
        return 'bg-gray-100 text-gray-800 dark:bg-gray-900/30 dark:text-gray-400';
    }
  };

  const getScoreColor = (score: number) => {
    if (score >= 8) return 'text-green-600 dark:text-green-400';
    if (score >= 5) return 'text-yellow-600 dark:text-yellow-400';
    return 'text-gray-600 dark:text-gray-400';
  };

  return (
    <Link
      to={getLocalizedPath(tool.path)}
      className="group block"
    >
      <Card className="h-full hover:shadow-xl transition-all duration-300 hover:border-primary-300 dark:hover:border-primary-600 hover:-translate-y-1 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-xl overflow-hidden relative">
        {/* Rank Badge */}
        <div className="absolute top-3 right-3 z-10">
          <div className="bg-primary-100 dark:bg-primary-900/30 text-primary-700 dark:text-primary-300 text-xs font-bold px-2 py-1 rounded-full">
            #{rank}
          </div>
        </div>

        <CardContent className="p-6">
          <div className="flex items-start space-x-4">
            {/* Tool Icon */}
            <div className="w-14 h-14 bg-gradient-to-br from-gray-100 to-gray-200 dark:from-gray-700 dark:to-gray-600 rounded-xl flex items-center justify-center flex-shrink-0 group-hover:from-primary-100 group-hover:to-primary-200 dark:group-hover:from-primary-900/30 dark:group-hover:to-primary-800/30 transition-all duration-300 shadow-sm">
              <ToolIcon className="w-7 h-7 text-gray-600 dark:text-gray-300 group-hover:text-primary-600 dark:group-hover:text-primary-400 transition-colors duration-300" />
            </div>

            <div className="flex-1 min-w-0">
              {/* Tool Name */}
              <CardTitle className="text-lg mb-2 line-clamp-1 group-hover:text-primary-600 dark:group-hover:text-primary-400 transition-colors duration-300 font-semibold">
                <SearchHighlight 
                  text={t(tool.name)} 
                  searchTerm={searchQuery}
                />
              </CardTitle>

              {/* Tool Description */}
              <CardDescription className="text-sm line-clamp-2 leading-relaxed mb-4">
                <SearchHighlight 
                  text={t(tool.description)} 
                  searchTerm={searchQuery}
                />
              </CardDescription>

              {/* Match Information */}
              <div className="flex items-center justify-between">
                <div className="flex flex-wrap gap-1">
                  {matchedFields.map((field) => (
                    <span
                      key={field}
                      className={`text-xs px-2 py-1 rounded-full font-medium ${getMatchBadgeColor(field)}`}
                    >
                      {field}
                    </span>
                  ))}
                </div>

                <div className="flex items-center space-x-2">
                  {/* Score */}
                  <div className="flex items-center">
                    <Zap className={`w-3 h-3 mr-1 ${getScoreColor(score)}`} />
                    <span className={`text-xs font-medium ${getScoreColor(score)}`}>
                      {score.toFixed(1)}
                    </span>
                  </div>

                  {/* Featured Badge */}
                  {tool.featured && (
                    <div className="flex items-center">
                      <Star className="w-3 h-3 text-yellow-500 mr-1" />
                      <span className="text-xs text-yellow-600 dark:text-yellow-400 font-medium">
                        {t('home.featured')}
                      </span>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>

          {/* Hover Effect */}
          <div className="absolute inset-0 bg-gradient-to-r from-primary-500/5 to-blue-500/5 opacity-0 group-hover:opacity-100 transition-opacity duration-300 rounded-xl" />
          
          {/* External Link Icon */}
          <div className="absolute bottom-3 right-3 opacity-0 group-hover:opacity-100 transition-opacity duration-300">
            <ExternalLink className="w-4 h-4 text-primary-500" />
          </div>
        </CardContent>
      </Card>
    </Link>
  );
};
